import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

export const WA_DEFAULTS = {
  // Default to the company line so chat works out of the box; admins can
  // override this in Admin → WhatsApp (stored in settings/whatsapp).
  number: '254141988977',
  inquiryTemplate:
    'Hi Follex Tech! I\'m interested in *{product}*{size}{color}. Could you share more details, availability and price? 😊',
  serviceTemplate:
    'Hi Follex Tech! I\'d like to enquire about your *{service}* service. Could you share more details and a quote? 😊',
  footerMessage:
    'Hi Follex Tech! I\'d like to know more about your ICT & electrical services. Could you help me out? 😊',
  contactMessage:
    'Hi! I have a question for the Follex Tech team — could you help me out? 😊',
};

// Module-level cache so Firestore is read at most once per browser session.
let _cache = null;
let _promise = null;

const _fetch = () => {
  if (!_promise) {
    _promise = getDoc(doc(db, 'settings', 'whatsapp'))
      .then((snap) => {
        _cache = snap.exists()
          ? { ...WA_DEFAULTS, ...snap.data() }
          : { ...WA_DEFAULTS };
        return _cache;
      })
      .catch(() => {
        _cache = { ...WA_DEFAULTS };
        _promise = null;
        return _cache;
      });
  }
  return _promise;
};

// Call this after admin saves so the cache is refreshed on next use.
export const invalidateWhatsAppCache = () => {
  _cache = null;
  _promise = null;
};

export const useWhatsAppSettings = () => {
  const [settings, setSettings] = useState(_cache || WA_DEFAULTS);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    if (_cache) { setSettings(_cache); setLoading(false); return; }
    _fetch().then((data) => { setSettings(data); setLoading(false); });
  }, []);

  // Opens WhatsApp with a pre-filled message.
  const openWhatsApp = (message) => {
    const number = (settings.number || '').replace(/[^0-9]/g, '');
    if (!number) return;
    window.open(
      `https://wa.me/${number}?text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  // Builds a service inquiry message from the service template.
  const buildServiceMessage = (serviceName) => {
    const tpl = settings.serviceTemplate || WA_DEFAULTS.serviceTemplate;
    return tpl.replace('{service}', serviceName || 'your services');
  };

  // Builds the product inquiry message from the template.
  const buildInquiryMessage = (product, selectedSize, selectedColor) => {
    let msg = settings.inquiryTemplate || WA_DEFAULTS.inquiryTemplate;
    msg = msg.replace('{product}', product?.name || 'your product');
    msg = msg.replace('{size}', selectedSize ? ` — Size: ${selectedSize}` : '');
    msg = msg.replace('{color}', selectedColor ? ` — Colour: ${selectedColor}` : '');
    msg = msg.replace('{price}',
      product?.price ? `KSh ${product.price.toFixed(0)}` : '');
    return msg;
  };

  const hasNumber = Boolean((settings.number || '').replace(/[^0-9]/g, ''));

  return { settings, loading, openWhatsApp, buildInquiryMessage, buildServiceMessage, hasNumber };
};
